import { ActiveProjectsConfig as ActiveProjectsConfigForm } from '../../dashboard-config';
import type { DashboardConfig } from '../types';
import { useDashboardConfigForm } from '../hooks/useDashboardConfigForm';
import { ConfigDialogShell } from './ConfigDialogShell';

interface ActiveProjectsConfigDialogProps {
  open: boolean;
  onClose: () => void;
  dashboardConfig: DashboardConfig;
  availableStages: string[];
  onSave: (config: DashboardConfig) => Promise<void>;
  reloadConfig: () => Promise<DashboardConfig>;
}

/**
 * Shared dialog for both the "Active Projects" and "Value in Progress"
 * tiles. Value in Progress sums over the same "what counts as active?"
 * scope, so keeping one config avoids desync between the two tiles.
 */
export function ActiveProjectsConfigDialog({
  open,
  onClose,
  dashboardConfig,
  availableStages,
  onSave,
  reloadConfig,
}: ActiveProjectsConfigDialogProps) {
  const { form, values, patch, submit, saving, error } = useDashboardConfigForm({
    open,
    onClose,
    dashboardConfig,
    onSave,
    reloadConfig,
  });
  return (
    <ConfigDialogShell
      open={open}
      title="Configure Active Projects & Value in Progress"
      onClose={onClose}
      onSave={submit}
      saving={saving}
      saveLabel={form.saveLabel('Save')}
      banner={form.banner}
      error={error}
      note={
        form.field('activeProjectsConfig', {
          format: (c) =>
            `${c.excludeCompleted ? 'completed projects excluded' : 'completed projects included'}, ${
              c.selectedStages.length ? c.selectedStages.join(', ') : 'no stages selected'
            }`,
        }).helperText
      }
    >
      <ActiveProjectsConfigForm
        value={values.activeProjectsConfig}
        availableStages={availableStages || []}
        onChange={(config) => patch({ activeProjectsConfig: config })}
        variant="compact"
      />
    </ConfigDialogShell>
  );
}
